import { forwardRef } from "react";
import { clsx } from "clsx";
import { useControlled } from "@exonn/hooks";
import { css } from "@emotion/react";
import { SwitchV2TestId } from "src/components/SwitchV2/SwitchV2.constants.ts";
import { useControlLabelV2Context } from "src/components/ControlLabelV2/ControlLabelV2.context.ts";
import type { InputHTMLAttributes, ClassAttributes } from "react";
import type { ChangeEvent, CSSProperties, Ref } from "react";
import type { FontWeightProps, MarginProps } from "styled-system";
import type { ExactTheme } from "src/lib/emotion/theme.ts";

interface SwitchBaseProps
  extends InputHTMLAttributes<HTMLInputElement>,
    ClassAttributes<HTMLInputElement>,
    MarginProps<ExactTheme>,
    FontWeightProps<ExactTheme> {}

export type SwitchV2Props = SwitchBaseProps;

const COMPONENT_NAME = "switch";

const switchClasses = {
  root: COMPONENT_NAME + "switch-root",
  checked: COMPONENT_NAME + "switch-checked",
  disabled: COMPONENT_NAME + "switch-disabled",
  thumb: COMPONENT_NAME + "switch-thumb",
  track: COMPONENT_NAME + "switch-track",
  input: COMPONENT_NAME + "switch-input",
};

export const SwitchV2 = forwardRef(
  (props: SwitchV2Props, ref: Ref<HTMLInputElement>) => {
    const {
      id: idProp,
      name,
      checked: checkedProp,
      defaultChecked,
      disabled: disabledProp = false,
      onChange,
      ...rest
    } = props;

    const { id = idProp, disabled = disabledProp } =
      useControlLabelV2Context() || {};

    const [checked, setChecked] = useControlled({
      controlled: checkedProp,
      default: !!defaultChecked,
      state: "checked",
      name: COMPONENT_NAME,
    });

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      onChange?.(e);
      setChecked(e.target.checked);
    };

    return (
      <div css={rootStyles({ disabled })}>
        <div
          className={clsx([
            switchClasses.root,
            checked && switchClasses.checked,
            disabled && switchClasses.disabled,
          ])}
          {...rest}
        >
          <div
            css={trackStyles({ disabled, checked })}
            className={switchClasses.track}
          />

          <div
            css={thumbStyles({ disabled, checked })}
            className={switchClasses.thumb}
          />

          <input
            ref={ref}
            type="checkbox"
            data-testid={SwitchV2TestId.Input}
            css={inputStyles()}
            className={switchClasses.input}
            id={id}
            name={name}
            checked={checked}
            disabled={disabled}
            onChange={handleChange}
          />
        </div>
      </div>
    );
  },
);

const trackSizeStyles: CSSProperties = {
  width: "35px",
  height: "20px",
  borderRadius: "10px",
};

const trackStyles =
  ({ checked, disabled }: { checked: boolean; disabled: boolean }) =>
  ({ colorsV2, inputBorderV2 }: ExactTheme) =>
    css({
      ...trackSizeStyles,
      willChange: "background",
      transition: "background 0.3s",

      background: checked
        ? disabled
          ? colorsV2.grey[100]
          : colorsV2.blue[500]
        : colorsV2.white,

      border: checked
        ? "none"
        : disabled
        ? inputBorderV2.solid.disabled.default
        : inputBorderV2.solid.standard.default,
    });

const thumbStyles =
  ({ checked, disabled }: { checked: boolean; disabled: boolean }) =>
  ({ colorsV2 }: ExactTheme) =>
    css({
      position: "absolute",
      width: "16px",
      height: "16px",
      left: "2px",
      top: "50%",
      borderRadius: "50%",
      boxShadow: "0 0 0 0 rgba(213, 231, 252, 0.5)",
      willChange: "transform, box-shadow, background",
      transition: "transform 0.15s, box-shadow 0.25s, background 0.3s",

      transform: checked
        ? "translate3d(calc(100% - 1px), -50%, 0)"
        : "translate3d(0, -50%, 0)",

      background: checked
        ? colorsV2.white
        : disabled
        ? colorsV2.grey[100]
        : colorsV2.blue[500],
    });

const rootStyles = ({ disabled }: { disabled: boolean }) =>
  css({
    position: "relative",
    cursor: disabled ? "default" : "pointer",
    width: "fit-content",
    height: "fit-content",

    ":hover": {
      [`.${switchClasses.thumb}`]: !disabled && {
        boxShadow: "0 0 0 5px rgba(213, 231, 252, 0.5)",
      },
    },

    ":active": {
      [`.${switchClasses.thumb}`]: !disabled && {
        boxShadow: "0 0 0 5px rgba(213, 231, 252, 0.9)",
      },
    },
  });

const inputStyles = () =>
  css({
    ...trackSizeStyles,
    appearance: "none",
    position: "absolute",
    opacity: 0,
    left: 0,
    top: 0,
    cursor: "inherit",
  });
